import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { IconAlertTriangle, IconLoader2 } from "@tabler/icons-react"
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle,
  Clock,
  Download,
  Package,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"
import { toastApiError } from "../../api/apiError"
import type {
  ActivityEntry,
  DeclarationTarget,
  QuestionnaireRow,
} from "../../api/customerRequestsApi"
import {
  daysUntilLabel,
  dueDateTone,
  useDeclarationTarget,
  useQuestionnaireFileDownload,
  useStartProcessing,
} from "../../api/customerRequestsApi"
import { intlLocale } from "../../utils/date"
import { inquiryRegulationLabels } from "../../utils/inquiryScope"
import { HandoffCard } from "../shared/HandoffCard"
import { KvRow } from "../shared/KvRow"
import { McPill } from "../shared/McPill"
import { SectionLabel } from "../shared/SectionLabel"
import { RecordRegulations } from "./RequestRegulations"
import { conformityCreatePath, customerRequestProcessPath } from "./routes"

type TranslateFn = (
  key: TranslationKeys,
  values?: Record<string, string | number>,
) => string

// activity.detailKey -> the translated free-text fragment for events whose
// existing template already interpolates a "detail" string (uploaded,
// imported). A detailKey missing from this map (e.g. ai_detected, whose
// backend emitter is outside this fix's scope) falls back to `detail`
// (English `notes`).
const ACTIVITY_DETAIL_TEXT_KEYS: Partial<Record<string, TranslationKeys>> = {
  UPLOADED: "MC_Q_RECORD_ACTIVITY_DETAIL_FILE",
  IMPORTED: "MC_Q_RECORD_ACTIVITY_DETAIL_IMPORTED",
}

/** Localized sentence for an activity row whose key is fixed by its action; rows
 *  written before the backend persisted the structured values carry no detailKey
 *  and show the English audit note instead of a sentence with blank placeholders. */
function activitySentence(
  a: ActivityEntry,
  key: TranslationKeys,
  translate: TranslateFn,
): string {
  return a.detailKey ? translate(key, a.detailParams) : (a.detail ?? "")
}

function activityDetailText(a: ActivityEntry, translate: TranslateFn): string {
  const key = a.detailKey ? ACTIVITY_DETAIL_TEXT_KEYS[a.detailKey] : undefined
  if (key) return translate(key, a.detailParams)
  return a.detail ?? ""
}

/** A field edit as people read it. The audit row keeps the raw field name and
 *  values; a regulations edit stores canonical keys (`REACH_SVHC, ROHS`), shown
 *  here with the field's label and the regulations' short labels, like the pills. */
function fieldUpdateText(
  a: ActivityEntry,
  translate: TranslateFn,
): {
  field: string
  oldValue: string | null | undefined
  newValue: string | null | undefined
} {
  if (a.field !== "regulation_keys") {
    return { field: a.field ?? "", oldValue: a.oldValue, newValue: a.newValue }
  }
  const labels = (value: string | null | undefined) =>
    value
      ? inquiryRegulationLabels(value.split(", ").filter(Boolean), translate)
          .map((regulation) => regulation.label)
          .join(", ")
      : value
  return {
    field: translate("MC_Q_FIELD_FRAMEWORK"),
    oldValue: labels(a.oldValue),
    newValue: labels(a.newValue),
  }
}

/** Where the generate screen opens for this prefill: the matched products when
 *  the codes could be placed, the whole customer when none could. With regulation
 *  settings on, the request's regulations go along as the preselection. */
function declarationPathFor(
  target: DeclarationTarget,
  withRegulations: boolean,
): string {
  const regulationKeys = withRegulations ? target.regulationKeys : undefined
  return target.products.length > 0
    ? conformityCreatePath({
        scope: "product",
        ids: target.products.map((p) => p.productId),
        regulationKeys,
      })
    : conformityCreatePath({
        scope: "customer",
        ids: [target.customerId],
        regulationKeys,
      })
}

interface RecordViewProps {
  row: QuestionnaireRow
  onBack: () => void
}

export function RecordView({ row, onBack }: RecordViewProps) {
  const { translate, currentLanguage } = useTranslation()
  const regulationSettingsOn = useFeatureFlag("MC_REGULATION_SETTINGS")
  const { hasPermission } = useUser()
  const activity = row.activity
  const { mutateAsync: getFileDownload } = useQuestionnaireFileDownload()
  const navigate = useNavigate()
  const { mutateAsync: startProcessing, isPending: startingProcessing } =
    useStartProcessing()
  const { mutateAsync: fetchDeclarationTarget, isPending: loadingTarget } =
    useDeclarationTarget()
  // Held rather than navigated to, while the reviewer decides about codes we
  // could not place.
  const [pendingTarget, setPendingTarget] = useState<DeclarationTarget | null>(
    null,
  )

  const openFile = async () => {
    try {
      const dl = await getFileDownload(row.id)
      window.open(dl.url, "_blank", "noopener")
    } catch (err) {
      toastApiError(err, translate, "MC_Q_RECORD_DOWNLOAD_FAILED")
    }
  }

  const statusPill = () => {
    if (row.status === "processed")
      return (
        <McPill tone="success" icon={Check}>
          {translate("MC_Q_RECORD_STATUS_PROCESSED")}
        </McPill>
      )
    if (row.status === "stored_pending")
      return (
        <McPill tone="muted" icon={Package}>
          {translate("MC_Q_RECORD_STATUS_PENDING")}
        </McPill>
      )
    if (row.status === "needs_confirm")
      return (
        <McPill tone="attention" icon={AlertCircle}>
          {translate("MC_Q_RECORD_STATUS_NEEDS_CONFIRM")}
        </McPill>
      )
    return null
  }

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <div className="mb-3.5">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft size={14} className="mr-1.5" />
          {translate("MC_Q_RECORD_BTN_BACK")}
        </Button>
      </div>
      <div className="rounded-xl border border-grey4 bg-grey2 p-7">
        <div className="mb-[18px] flex items-start justify-between gap-4">
          <div>
            <SectionLabel className="mb-1.5">
              {translate("MC_Q_RECORD_EYEBROW")}
            </SectionLabel>
            <h2 className="m-0 text-[20px] leading-tight font-medium text-white">
              {row.customer}
            </h2>
            <div className="mt-1.5 flex items-center gap-2.5 text-[12.5px] text-grey5">
              <span className="font-mono">{row.requestId}</span>
              <span>·</span>
              <span>
                {translate("MC_Q_RECORD_RECEIVED", { date: row.received })}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-start gap-1.5">
            {statusPill()}
          </div>
        </div>

        {/* Data fields */}
        <div className="mb-3.5 overflow-hidden rounded-lg border border-grey4 bg-grey2">
          <div className="border-b border-grey4 px-[18px] py-3">
            <SectionLabel>{translate("MC_Q_RECORD_DATA_TITLE")}</SectionLabel>
          </div>
          <div className="px-[18px] py-1">
            <KvRow
              k={translate("MC_Q_RECORD_KV_DOC")}
              v={
                <span className="flex items-center gap-1.5">
                  {row.doc}
                  <button
                    type="button"
                    className="text-yellow1 hover:underline"
                    onClick={() => void openFile()}
                  >
                    <Download size={11} />
                  </button>
                </span>
              }
            />

            {/* Regulations: canonical keys, editable, while regulation settings are
                on (TUR-1734); otherwise the detected wording, read-only. */}
            <div className="flex items-start justify-between gap-4 border-b border-grey4 py-1.5">
              <span className="shrink-0 pt-0.5 text-[12px] whitespace-nowrap text-grey5">
                {translate("MC_Q_RECORD_KV_FRAMEWORK")}
              </span>
              {regulationSettingsOn ? (
                <RecordRegulations
                  row={row}
                  canEdit={hasPermission(PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)}
                />
              ) : (
                <div className="flex flex-wrap justify-end gap-[4px]">
                  {row.frameworks.map((f) => (
                    <McPill key={f} tone="muted">
                      {f}
                    </McPill>
                  ))}
                </div>
              )}
            </div>

            <KvRow
              k={translate("MC_Q_RECORD_KV_ARTICLES")}
              v={translate("MC_Q_RECORD_KV_ARTICLES_VALUE", {
                count: row.articles,
              })}
              mono
            />

            {/* Due date — read-only */}
            <div className="flex items-center justify-between gap-4 border-b border-grey4 py-1.5">
              <span className="shrink-0 text-[12px] whitespace-nowrap text-grey5">
                {translate("MC_Q_RECORD_KV_DUE")}
              </span>
              <span className="text-right">
                <span
                  className={`font-mono text-[12.5px] ${dueDateTone(row.due) === "danger" ? "text-red1" : dueDateTone(row.due) === "warning" ? "text-warning" : "text-white"}`}
                >
                  {row.due}
                </span>
                <span
                  className={`ml-1.5 text-[10px] ${dueDateTone(row.due) === "danger" ? "text-red1" : dueDateTone(row.due) === "warning" ? "text-warning" : "text-grey5"}`}
                >
                  ({daysUntilLabel(row.due, translate)})
                </span>
              </span>
            </div>

            {/* Notes — read-only */}
            <div className="flex items-start justify-between gap-4 border-b border-grey4 py-1.5">
              <span className="shrink-0 pt-0.5 text-[12px] whitespace-nowrap text-grey5">
                {translate("MC_Q_RECORD_KV_NOTES")}
              </span>
              <span className="text-right text-[12.5px]">
                {row.notes || (
                  <span className="text-grey5 italic">
                    {translate("MC_Q_RECORD_NOTES_EMPTY")}
                  </span>
                )}
              </span>
            </div>

            <KvRow
              k={translate("MC_Q_RECORD_KV_CREATED_BY")}
              v={row.createdBy}
            />
            <KvRow
              k={translate("MC_Q_RECORD_KV_CREATED")}
              v={new Date(row.createdAt).toLocaleString(
                intlLocale(currentLanguage),
              )}
            />
            {row.docType && (
              <KvRow
                k={translate("MC_Q_RECORD_KV_DOC_TYPE")}
                v={<span className="text-grey5">{row.docType}</span>}
              />
            )}
          </div>
        </div>

        {/* Article codes (non-editable) */}
        <div className="mb-3.5 overflow-hidden rounded-lg border border-grey4 bg-grey2">
          <div className="border-b border-grey4 px-[18px] py-3">
            <SectionLabel>
              {translate("MC_Q_CODES_TITLE", {
                count: row.articleCodes.length,
              })}
            </SectionLabel>
          </div>
          <div className="px-[18px] py-3">
            <div className="flex flex-wrap gap-[5px]">
              {row.articleCodes.map((c) => (
                <span
                  key={c}
                  className="font-mono rounded-full border border-grey4 bg-grey3 px-2 py-0.5 text-[11px] text-white"
                >
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-grey5">
              {translate("MC_Q_RECORD_CODES_NOTE")}
            </div>
          </div>
        </div>

        {/* Activity log */}
        <div className="mb-3.5 overflow-hidden rounded-lg border border-grey4 bg-grey2">
          <div className="border-b border-grey4 px-[18px] py-3">
            <SectionLabel>
              {translate("MC_Q_RECORD_ACTIVITY_TITLE")}
            </SectionLabel>
          </div>
          <div className="px-[18px] py-2">
            {activity.map((a, i) => {
              const uploadedDetail = activityDetailText(a, translate)
              const fieldUpdate = fieldUpdateText(a, translate)
              return (
                <div
                  key={i}
                  className="flex items-start gap-2.5 border-b border-grey4/40 py-1.5 last:border-0"
                >
                  <Clock size={12} className="mt-0.5 shrink-0 text-grey5" />
                  <div className="flex-1 text-[12px] text-grey5">
                    <span className="font-mono text-[10.5px]">
                      {a.timestamp
                        ? new Date(a.timestamp).toLocaleString(
                            intlLocale(currentLanguage),
                          )
                        : ""}
                    </span>
                    <span className="mx-1.5">—</span>
                    {a.action === "uploaded" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_UPLOADED")}{" "}
                        <span className="text-grey5">{a.actor}</span>
                        {uploadedDetail && (
                          <span className="text-grey5">
                            {" "}
                            · {uploadedDetail}
                          </span>
                        )}
                      </span>
                    )}
                    {a.action === "created" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_STORED")}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "imported" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_IMPORTED", {
                          detail: activityDetailText(a, translate),
                        })}
                      </span>
                    )}
                    {a.action === "ai_detected" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_AI_DETECTED", {
                          detail: a.detail ?? "",
                        })}
                      </span>
                    )}
                    {a.action === "field_updated" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_FIELD_UPDATED", {
                          field: fieldUpdate.field,
                        })}{" "}
                        <span className="text-grey5 line-through">
                          {fieldUpdate.oldValue}
                        </span>{" "}
                        →{" "}
                        <span className="text-yellow1">
                          {fieldUpdate.newValue}
                        </span>
                        <span className="text-grey5">
                          {" "}
                          {translate("MC_CS_CONF_EDIT_BY_ACTOR", {
                            actor: a.actor,
                          })}
                        </span>
                      </span>
                    )}
                    {a.action === "compliance_status_changed" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_STATUS_CHANGED")}{" "}
                        <span className="text-grey5">{a.oldValue}</span> →{" "}
                        <span className="text-yellow1">{a.newValue}</span>
                        <span className="text-grey5">
                          {" "}
                          {translate("MC_CS_CONF_EDIT_BY_ACTOR", {
                            actor: a.actor,
                          })}
                        </span>
                      </span>
                    )}
                    {a.action === "discarded" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_DISCARDED")}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "processing_started" && (
                      <span className="text-white">
                        {translate("MC_Q_RECORD_ACTIVITY_PROCESSING_STARTED")}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "mapping_confirmed" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_MAPPING_CONFIRMED",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "reconciliation_resolved" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_RECONCILIATION_RESOLVED",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "answer_overridden" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_ANSWER_OVERRIDDEN",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "stale_accepted" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_STALE_ACCEPTED",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "gap_refresh_requested" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_GAP_REFRESH_REQUESTED",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                    {a.action === "gap_sent_to_master_data" && (
                      <span className="text-white">
                        {activitySentence(
                          a,
                          "MC_Q_RECORD_ACTIVITY_GAP_SENT_MASTER_DATA",
                          translate,
                        )}{" "}
                        <span className="text-grey5">{a.actor}</span>
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {row.status === "stored_pending" && (
          <>
            <HandoffCard
              items={[
                translate("MC_Q_RECORD_HANDOFF_1"),
                translate("MC_Q_RECORD_HANDOFF_2"),
                translate("MC_Q_RECORD_HANDOFF_3"),
                translate("MC_Q_RECORD_HANDOFF_4"),
              ]}
            />
            <div className="mt-3 flex justify-start">
              <Button
                className="bg-yellow1 text-black hover:bg-yellow2"
                disabled={startingProcessing}
                onClick={async () => {
                  try {
                    await startProcessing(row.id)
                    navigate(customerRequestProcessPath(row.id))
                  } catch (err) {
                    toastApiError(
                      err,
                      translate,
                      "MC_Q_START_PROCESSING_FAILED",
                    )
                  }
                }}
              >
                {startingProcessing && (
                  <IconLoader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                {startingProcessing
                  ? translate("MC_Q_RECORD_STARTING_PROCESSING")
                  : translate("MC_Q_RECORD_START_PROCESSING")}
              </Button>
            </div>
          </>
        )}

        {/* A customer emailing "is part X RoHS compliant?" is not a questionnaire
            to answer — it is a declaration to issue. The classifier already read
            the customer, the frameworks and the article codes out of the file,
            so this hands them straight to the generate screen instead of leaving
            the reviewer to retype them (F1). A prefill, not a draft: issuing is a
            legal document and the scope is confirmed there. */}
        {row.docType === "certificate_request" && (
          <div className="mt-3 flex justify-start">
            <Button
              variant="outline"
              disabled={loadingTarget}
              onClick={async () => {
                try {
                  const target = await fetchDeclarationTarget(row.id)
                  // The backend reports codes it could not place instead of
                  // dropping them, precisely so this screen does not issue a
                  // compliance statement over the wrong set of products. Two
                  // ways that goes wrong, and both end in a legal document:
                  // some codes unplaced narrows the scope, none placed widens
                  // it from "these parts" to everything the customer buys. Ask
                  // before either; go straight through when every code matched.
                  if (
                    target.unmatchedCodes.length > 0 ||
                    target.products.length === 0
                  ) {
                    setPendingTarget(target)
                    return
                  }
                  navigate(declarationPathFor(target, regulationSettingsOn))
                } catch (err) {
                  toastApiError(err, translate, "MC_Q_DECL_TARGET_FAILED")
                }
              }}
            >
              {loadingTarget && (
                <IconLoader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              {translate("MC_Q_RECORD_CREATE_DECLARATION")}
            </Button>
          </div>
        )}

        {pendingTarget && (
          <ConfirmDialog
            variant="warning"
            icon={IconAlertTriangle}
            title={translate(
              pendingTarget.products.length > 0
                ? "MC_Q_DECL_SOME_CODES_TITLE"
                : "MC_Q_DECL_NO_CODES_TITLE",
            )}
            message={translate(
              pendingTarget.products.length > 0
                ? "MC_Q_DECL_SOME_CODES_MSG"
                : "MC_Q_DECL_NO_CODES_MSG",
              {
                matched: pendingTarget.products.length,
                unmatched: pendingTarget.unmatchedCodes.length,
                codes: pendingTarget.unmatchedCodes.join(", "),
              },
            )}
            confirmLabel={translate("MC_Q_DECL_CONTINUE")}
            cancelLabel={translate("MC_DECL_BTN_CANCEL")}
            onConfirm={() => {
              const target = pendingTarget
              setPendingTarget(null)
              navigate(declarationPathFor(target, regulationSettingsOn))
            }}
            onCancel={() => setPendingTarget(null)}
          />
        )}

        {row.status === "processed" && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-success/25 bg-success/6 p-3.5">
            <div className="flex items-start gap-3">
              <CheckCircle size={16} className="mt-0.5 shrink-0 text-success" />
              <div className="text-[12.5px] leading-[1.55] text-white">
                {translate("MC_Q_RECORD_DONE_MSG")}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(customerRequestProcessPath(row.id))}
            >
              {translate("MC_Q_RECORD_CONTINUE_PROCESSING")}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
